import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { plannerVersion, type PlannerVersion } from './confidence'
import type { StagedAction } from './types'

type StagedActionEventSubject = Pick<StagedAction, 'id' | 'toolName' | 'toolSet'> &
  Partial<Pick<StagedAction, 'metadata' | 'sourceMetadata'>>

export interface StagedActionEventProperties {
  actionId: string
  toolName: string | undefined
  toolSet: string | undefined
  planner: PlannerVersion
}

/** Every `Staged Action` event spreads this, so v1 and v2 rates break down on the same `planner` property. */
export function stagedActionEventProperties(action: StagedActionEventSubject): StagedActionEventProperties {
  return {
    actionId: action.id,
    toolName: action.toolName,
    toolSet: action.toolSet,
    planner: plannerVersion(action),
  }
}

interface ActionEventContext {
  /** Amplitude `source` for the surface that hosted the click, e.g. 'cell' or 'action-view'. */
  source?: string
  trigger?: string
  /** Names a region within the surface when one surface has more than one, e.g. 'nested-view'. */
  surface?: string
}

function trackActionEvent(action: StagedAction, name: string, { source, trigger, surface }: ActionEventContext) {
  AmplitudeRenderer.trackEvent('Staged Action', {
    action: name,
    ...stagedActionEventProperties(action),
    ...(source && { source }),
    ...(trigger && { trigger }),
    ...(surface && { surface }),
  })
}

/**
 * Copying a draft instead of invoking it means the user is taking the output
 * somewhere Highlight can't see. Reported under the same `Staged Action` event
 * as view/edit/invoke so copy rate and invoke rate stay comparable.
 */
export function trackStagedActionCopy(
  action: StagedAction,
  target: 'content' | 'link',
  context: ActionEventContext = {},
): void {
  trackActionEvent(action, target === 'content' ? 'copy content' : 'copy link', context)
}

/** Following a result link is the closest signal we have that an executed action was acted on. */
export function trackStagedActionOpenLink(action: StagedAction, context: ActionEventContext = {}): void {
  trackActionEvent(action, 'open link', context)
}

export type StagedActionShownSurface = 'action-view' | 'chat-output'

interface StagedActionShownContext {
  surface: StagedActionShownSurface
  /** The view-history entry showing it, so going back to the same entry doesn't count again. */
  entryKey: number
  /** The `sourceLabel` of whatever opened the view, e.g. 'For You', 'Chat', 'History'. */
  openedFrom?: string
}

const shownActions = new Set<string>()

/**
 * Sent by the views that display an action, once per action per view entry.
 * `view` comes from the controls that open an action, so it misses every open
 * without one: sidebar conversation rows, chat, history, search, deep links.
 */
export function trackStagedActionShown(
  action: StagedActionEventSubject,
  { surface, entryKey, openedFrom }: StagedActionShownContext,
): void {
  const key = `${surface}:${entryKey}:${action.id}`
  if (shownActions.has(key)) return
  shownActions.add(key)
  AmplitudeRenderer.trackEvent('Staged Action', {
    action: 'shown',
    ...stagedActionEventProperties(action),
    source: surface,
    ...(openedFrom && { openedFrom }),
  })
}
